import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { logger } from '../utils/logger';
import type { UserRole } from '../types';

// Define the role options with descriptions
const roleOptions: { role: UserRole; title: string; description: string; available: boolean }[] = [
  {
    role: 'candidate',
    title: 'Candidate',
    description: 'Take coding exercises and complete interview tasks',
    available: true,
  },
  {
    role: 'interviewer',
    title: 'Interviewer',
    description: 'Create tasks and review candidate submissions (requires an Organization ID)',
    available: true,
  },
  {
    role: 'orgadmin',
    title: 'Organization Admin',
    description: 'Create and manage your organization (invite interviewers & candidates)',
    available: true,
  },
  {
    role: 'admin',
    title: 'Platform Admin',
    description: 'Full platform administration (internal use only)',
    available: false, // Only available through backend assignment
  },
];

const UserTypeSelection: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, updateUserRole } = useAuth();
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [organizationId, setOrganizationId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
  };

  const handleContinue = async () => {
    if (!selectedRole) {
      logger.warn('Please select a role to continue');
      return;
    }

    if (!isAuthenticated) {
      logger.error('Authentication required');
      navigate('/login');
      return;
    }

    // For interviewer accounts ensure an organisation ID is provided
    if (selectedRole === 'interviewer' && !organizationId.trim()) {
      logger.warn('Organization ID is required for interviewer accounts');
      return;
    }

    setLoading(true);

    try {
      // Persist role (and ideally organizationId via backend). For now we only update the role.
      await updateUserRole(selectedRole);

      // Store organisation ID temporarily if provided (until backend integration).
      if (organizationId) {
        sessionStorage.setItem('organization_id', organizationId);
      }

      logger.info(`Role selected: ${selectedRole}`);

      // Navigate to respective dashboard / next onboarding step
      if (selectedRole === 'candidate') {
        navigate('/dashboard');
      } else if (selectedRole === 'interviewer') {
        navigate('/interviewer/dashboard');
      } else if (selectedRole === 'orgadmin') {
        navigate('/organization-form');
      } else if (selectedRole === 'admin') {
        navigate('/admin/dashboard');
      }
    } catch (error) {
      logger.error('Failed to update user role', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Select your role
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Choose how you'll use SkillFoundry
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-4">
            {roleOptions.map((option) => (
              <div
                key={option.role}
                className={`relative rounded-lg border ${
                  selectedRole === option.role
                    ? 'border-blue-500 ring-2 ring-blue-200'
                    : 'border-gray-300 hover:border-gray-400'
                } bg-white p-4 cursor-pointer`}
                onClick={() => option.available && handleRoleSelect(option.role)}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0 inline-flex items-center justify-center h-6 w-6">
                    {selectedRole === option.role && (
                      <svg
                        className="h-5 w-5 text-blue-500"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3 text-sm">
                    <p className="font-medium text-gray-900">{option.title}</p>
                    <p className="text-gray-500">{option.description}</p>
                    {!option.available && (
                      <p className="text-xs text-red-500 mt-1">
                        This role is not available for self-registration
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Organization ID field for Interviewers */}
          {selectedRole === 'interviewer' && (
            <div className="mt-4">
              <label htmlFor="org-id" className="block text-sm font-medium text-gray-700">
                Organization ID (from your email invite)
              </label>
              <input
                type="text"
                id="org-id"
                name="org-id"
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                placeholder="e.g. 5f1a2b3c4d"
                className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          <div className="mt-6">
            <button
              onClick={handleContinue}
              disabled={!selectedRole || loading}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                !selectedRole || loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {loading ? 'Processing...' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserTypeSelection; 